import { DownloadOutlined, SearchOutlined, LinkOutlined } from '@ant-design/icons';
import { Button, Select, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import apiReconciliation from 'apps/investment/apiCalls/order/reconciliation';
import apiOrderList from 'apps/investment/apiCalls/order/list';
import { fundDistributorsOptions } from 'apps/investment/constants/fund-distributors';
import {
  fundOrderSideColors,
  fundOrderSideLabels,
} from 'apps/investment/constants/fund-orders';
import { orderStatusColors } from 'apps/investment/constants/fundOrder.js';
import DatePicker from 'common/ui/UIDatePicker';
import { useFetchApiGet } from 'hooks/api/useFetchApi';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from 'config/env-config';
import { getAccessToken, staffSessionHeaders } from 'utils/auth';

const { Title } = Typography;
const { MonthPicker } = DatePicker as any;

const MONTH_FORMAT = 'YYYY-MM';
const PAGE_SIZE = 20;

const managerTagColors: Record<string, string> = {
  fmarket: 'blue',
  vcam: 'green',
  nhsv: 'purple',
};

const formatAmount = (value: number) =>
  value.toLocaleString('vi-VN', { minimumFractionDigits: 0 });

const formatQuantity = (value: number) =>
  value.toLocaleString('vi-VN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });

const orderSourceColors: Record<string, string> = {
  stag: 'blue',
  fmarket: 'cyan',
  vcam: 'green',
  nhsv: 'purple',
};

const summaryColumns = [
  {
    title: 'Nhà phân phối',
    dataIndex: 'trading_account_manager',
    key: 'trading_account_manager',
    width: 140,
    render: (value: string) => (
      <Tag color={managerTagColors[value] || 'default'}>{value?.toUpperCase()}</Tag>
    ),
  },
  {
    title: 'Tháng',
    dataIndex: 'month',
    key: 'month',
    width: 90,
  },
  {
    title: 'Quỹ',
    dataIndex: 'fund_code',
    key: 'fund_code',
    width: 120,
  },
  {
    title: 'Nguồn lệnh',
    dataIndex: 'order_source',
    key: 'order_source',
    width: 90,
    render: (v: string) => <Tag color={orderSourceColors[v] || 'default'}>{v}</Tag>,
  },
  {
    title: 'Tài khoản',
    dataIndex: 'account_source',
    key: 'account_source',
    width: 120,
    render: (v: string) => (v ? <Tag color={orderSourceColors[v] || 'default'}>{v}</Tag> : '-'),
  },
  {
    title: 'Tổng lệnh',
    dataIndex: 'total_orders',
    key: 'total_orders',
    align: 'right' as const,
    width: 90,
  },
  {
    title: 'Lệnh mua',
    dataIndex: 'total_buy_orders',
    key: 'total_buy_orders',
    align: 'right' as const,
    width: 90,
    render: (v: number) => <span style={{ color: '#389e0d' }}>{v}</span>,
  },
  {
    title: 'Lệnh bán',
    dataIndex: 'total_sell_orders',
    key: 'total_sell_orders',
    align: 'right' as const,
    width: 90,
    render: (v: number) => <span style={{ color: '#cf1322' }}>{v}</span>,
  },
  {
    title: 'Giá trị mua (VND)',
    dataIndex: 'total_buy_amount',
    key: 'total_buy_amount',
    align: 'right' as const,
    render: formatAmount,
  },
  {
    title: 'Giá trị bán (VND)',
    dataIndex: 'total_sell_amount',
    key: 'total_sell_amount',
    align: 'right' as const,
    render: formatAmount,
  },
  {
    title: 'Giá trị mua ròng (VND)',
    dataIndex: 'total_net_buy_amount',
    key: 'total_net_buy_amount',
    align: 'right' as const,
    render: formatAmount,
  },
  {
    title: 'Giá trị bán ròng (VND)',
    dataIndex: 'total_net_sell_amount',
    key: 'total_net_sell_amount',
    align: 'right' as const,
    render: formatAmount,
  },
  {
    title: 'SL mua (CCQ)',
    dataIndex: 'total_buy_quantity',
    key: 'total_buy_quantity',
    align: 'right' as const,
    render: formatQuantity,
  },
  {
    title: 'SL bán (CCQ)',
    dataIndex: 'total_sell_quantity',
    key: 'total_sell_quantity',
    align: 'right' as const,
    render: formatQuantity,
  },
  {
    title: 'Phí (VND)',
    dataIndex: 'total_fee_amount',
    key: 'total_fee_amount',
    align: 'right' as const,
    render: formatAmount,
  },
  {
    title: 'Thuế (VND)',
    dataIndex: 'total_tax_amount',
    key: 'total_tax_amount',
    align: 'right' as const,
    render: formatAmount,
  },
];

const orderDetailColumns = [
  {
    title: '',
    dataIndex: 'id',
    key: 'id',
    width: 48,
    render: (id: string) => (
      <Link to={`/investment/fund-orders/${id}`} target="_blank">
        <LinkOutlined style={{ fontSize: 18 }} />
      </Link>
    ),
  },
  {
    title: 'Số tài khoản',
    dataIndex: 'trading_account_code',
    key: 'trading_account_code',
    width: 140,
  },
  {
    title: 'Tài khoản',
    dataIndex: 'account_source',
    key: 'account_source',
    width: 110,
    render: (v: string) => (v ? <Tag color={orderSourceColors[v] || 'default'}>{v}</Tag> : '-'),
  },
  {
    title: 'Order Date',
    dataIndex: 'order_date',
    key: 'order_date',
    width: 160,
    render: (v: string) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-'),
  },
  {
    title: 'Lệnh',
    dataIndex: 'order_side',
    key: 'order_side',
    width: 120,
    render: (v: string) => (
      <Tag color={fundOrderSideColors[v]}>{fundOrderSideLabels[v] || v}</Tag>
    ),
  },
  {
    title: 'Trạng thái',
    dataIndex: 'status',
    key: 'status',
    width: 160,
    render: (v: number, row: any) => (
      <Tag color={orderStatusColors[v]}>{row.status_label || v}</Tag>
    ),
  },
  {
    title: 'Quỹ',
    dataIndex: 'fund_code',
    key: 'fund_code',
    width: 120,
  },
  {
    title: 'Nhà phân phối',
    dataIndex: 'trading_account_manager',
    key: 'trading_account_manager',
    width: 130,
    render: (v: string) => (
      <Tag color={managerTagColors[v] || 'default'}>{v?.toUpperCase()}</Tag>
    ),
  },
  {
    title: 'Nguồn lệnh',
    dataIndex: 'order_source',
    key: 'order_source',
    width: 90,
    render: (v: string) => <Tag color={orderSourceColors[v] || 'default'}>{v}</Tag>,
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    align: 'right' as const,
    width: 130,
    render: (v: number) => formatAmount(v),
  },
  {
    title: 'Net Amount',
    dataIndex: 'net_amount',
    key: 'net_amount',
    align: 'right' as const,
    width: 130,
    render: (v: number) => formatAmount(v),
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    key: 'quantity',
    align: 'right' as const,
    width: 110,
    render: (v: number) => formatQuantity(v),
  },
  {
    title: 'Fund Order ID',
    dataIndex: 'fund_order_id',
    key: 'fund_order_id',
    width: 160,
  },
  {
    title: 'Customer',
    dataIndex: 'user',
    key: 'user',
    width: 200,
    render: (user: any) =>
      user ? `${user.name || ''} (${user.email || ''})` : '-',
  },
];

const distributorOptions = [
  { value: '', label: 'Tất cả' },
  ...fundDistributorsOptions,
];

const TAB_DETAIL = 'detail';
const TAB_SUMMARY = 'summary';

export default function FundOrderReconciliationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = location.pathname.endsWith('/summary') ? TAB_SUMMARY : TAB_DETAIL;

  const [tradingAccountManager, setTradingAccountManager] = useState(
    searchParams.get('trading_account_manager') || ''
  );
  const [orderSource, setOrderSource] = useState(
    searchParams.get('order_source') ?? 'stag'
  );
  const [accountSource, setAccountSource] = useState(
    searchParams.get('account_source') ?? 'stag'
  );
  const [fromMonth, setFromMonth] = useState<any>(
    searchParams.get('from_month')
      ? moment(searchParams.get('from_month'), MONTH_FORMAT)
      : moment().startOf('month')
  );
  const [toMonth, setToMonth] = useState<any>(
    searchParams.get('to_month')
      ? moment(searchParams.get('to_month'), MONTH_FORMAT)
      : moment()
  );
  const [ordersPage, setOrdersPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [hasSearched, setHasSearched] = useState(false);

  const {
    data: reconciliationData,
    isLoading: isLoadingReconciliation,
    load: loadReconciliation,
  } = useFetchApiGet(apiReconciliation, { resourceName: 'data' });

  const { data: ordersResponse, isLoading: isLoadingOrders, load: loadOrders } =
    useFetchApiGet(apiOrderList);

  const buildReconciliationParams = () => {
    const params: Record<string, string> = {};
    if (tradingAccountManager) params.trading_account_manager = tradingAccountManager;
    if (orderSource) params.order_source = orderSource;
    if (accountSource) params.account_source = accountSource;
    if (fromMonth) params.from_month = fromMonth.format(MONTH_FORMAT);
    if (toMonth) params.to_month = toMonth.format(MONTH_FORMAT);
    return params;
  };

  const buildOrdersParams = (page = 1) => {
    const params: Record<string, string> = {
      page: String(page),
      page_size: String(PAGE_SIZE),
      ordering: '-order_date',
    };
    if (orderSource) params.order_source = orderSource;
    if (accountSource) params.account_source = accountSource;
    if (tradingAccountManager) params.trading_account_manager = tradingAccountManager;
    if (fromMonth) params.from_month = fromMonth.format(MONTH_FORMAT);
    if (toMonth) params.to_month = toMonth.format(MONTH_FORMAT);
    return params;
  };

  const syncUrlParams = (page: number) => {
    const next: Record<string, string> = {};
    if (tradingAccountManager) next.trading_account_manager = tradingAccountManager;
    // Always written (even '' = "Tất cả") so a reload doesn't fall back to the 'stag' default.
    next.order_source = orderSource;
    next.account_source = accountSource;
    if (fromMonth) next.from_month = fromMonth.format(MONTH_FORMAT);
    if (toMonth) next.to_month = toMonth.format(MONTH_FORMAT);
    if (page > 1) next.page = String(page);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (searchParams.get('from_month') || searchParams.get('trading_account_manager')) {
      setHasSearched(true);
      loadReconciliation(buildReconciliationParams());
      loadOrders(buildOrdersParams(ordersPage));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    const page = 1;
    setOrdersPage(page);
    setHasSearched(true);
    syncUrlParams(page);
    loadReconciliation(buildReconciliationParams());
    loadOrders(buildOrdersParams(page));
  };

  const handleOrdersPageChange = (page: number) => {
    setOrdersPage(page);
    syncUrlParams(page);
    loadOrders(buildOrdersParams(page));
  };

  const handleTabChange = (key: string) => {
    const base = '/accounting/fund-order-reconciliation';
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
    navigate(key === TAB_SUMMARY ? `${base}/summary${qs}` : `${base}${qs}`);
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    const params = buildReconciliationParams();
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/api/admin/orders/reconciliation/export${query ? `?${query}` : ''}`;

    const { token } = getAccessToken();
    setIsExporting(true);
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...staffSessionHeaders(),
        },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `fund_order_reconciliation_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch {
      message.error('Xuất Excel thất bại. Vui lòng thử lại.');
    } finally {
      setIsExporting(false);
    }
  };

  const reconciliationRows: any[] = Array.isArray(reconciliationData) ? reconciliationData : [];
  // Account source lives on the fund account, not the order. With a source filter every
  // row shares it; with "Tất cả" the API sends each order's account_source.
  const orders: any[] = (ordersResponse?.data ?? []).map((o: any) => ({
    ...o,
    account_source: accountSource || o.account_source,
  }));
  const paging = ordersResponse?.paging ?? null;

  const filterBar = (
    <Space wrap style={{ marginBottom: 16 }}>
      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Nhà phân phối</div>
        <Select
          style={{ width: 160 }}
          value={tradingAccountManager}
          onChange={setTradingAccountManager}
          options={distributorOptions}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Nguồn tài khoản</div>
        <Select
          style={{ width: 130 }}
          value={accountSource}
          onChange={setAccountSource}
          options={[
            { value: '', label: 'Tất cả' },
            { value: 'stag', label: 'Stag' },
            { value: 'fmarket', label: 'Fmarket' },
            { value: 'vcam', label: 'VCAM' },
          ]}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Nguồn lệnh</div>
        <Select
          style={{ width: 130 }}
          value={orderSource}
          onChange={setOrderSource}
          options={[
            { value: '', label: 'Tất cả' },
            { value: 'stag', label: 'Stag' },
            { value: 'fmarket', label: 'Fmarket' },
            { value: 'vcam', label: 'VCAM' },
            { value: 'nhsv', label: 'NHSV' },
          ]}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Từ tháng</div>
        <MonthPicker
          value={fromMonth}
          onChange={setFromMonth}
          format="MM/YYYY"
          placeholder="Từ tháng"
          style={{ width: 140 }}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Đến tháng</div>
        <MonthPicker
          value={toMonth}
          onChange={setToMonth}
          format="MM/YYYY"
          placeholder="Đến tháng"
          style={{ width: 140 }}
        />
      </div>

      <div style={{ marginTop: 20 }}>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={isLoadingReconciliation || isLoadingOrders}
        >
          Tìm kiếm
        </Button>
      </div>

      <div style={{ marginTop: 20 }}>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={isExporting}
          disabled={!hasSearched}
        >
          Xuất Excel
        </Button>
      </div>
    </Space>
  );


  const tabItems = [
    {
      key: TAB_DETAIL,
      label: `Chi tiết lệnh${paging ? ` (${paging.count})` : ''}`,
      children: (
        <Table
          dataSource={orders}
          columns={orderDetailColumns}
          rowKey="id"
          size="small"
          scroll={{ x: 1850 }}
          loading={isLoadingOrders}
          locale={{ emptyText: hasSearched ? 'Không có lệnh nào' : 'Chọn bộ lọc và nhấn Tìm kiếm' }}
          pagination={{
            current: ordersPage,
            pageSize: PAGE_SIZE,
            total: paging?.count ?? 0,
            showSizeChanger: false,
            showTotal: (total) => `${total} lệnh`,
            onChange: handleOrdersPageChange,
          }}
        />
      ),
    },
    {
      key: TAB_SUMMARY,
      label: `Tổng hợp${reconciliationRows.length ? ` (${reconciliationRows.length})` : ''}`,
      children: (
        <Table
          dataSource={reconciliationRows}
          columns={summaryColumns}
          rowKey={(record: any) =>
            `${record.trading_account_manager}_${record.month}_${record.fund_code}_${record.account_source}`
          }
          pagination={false}
          size="small"
          scroll={{ x: 1520 }}
          locale={{
            emptyText: hasSearched ? 'Không có dữ liệu' : 'Chọn bộ lọc và nhấn Tìm kiếm',
          }}
          loading={isLoadingReconciliation}
        />
      ),
    },
  ];

  const totalOrders = reconciliationRows.reduce((s, r) => s + (r.total_orders ?? 0), 0);
  const totalBuy = reconciliationRows.reduce((s, r) => s + (r.total_buy_amount ?? 0), 0);
  const totalSell = reconciliationRows.reduce((s, r) => s + (r.total_sell_amount ?? 0), 0);
  const totalGD = totalBuy + totalSell;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Đối soát lệnh quỹ</Title>
      {filterBar}
      {reconciliationRows.length > 0 && (
        <div style={{ display: 'flex', gap: 32, marginBottom: 16, padding: '12px 16px', background: '#f5f5f5', borderRadius: 6 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng số lệnh</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{totalOrders.toLocaleString('vi-VN')}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng giá trị GD</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{totalGD.toLocaleString('vi-VN')} ₫</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng mua</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#389e0d' }}>{totalBuy.toLocaleString('vi-VN')} ₫</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng bán</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#cf1322' }}>{totalSell.toLocaleString('vi-VN')} ₫</div>
          </div>
        </div>
      )}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
    </div>
  );
}
